import type { ErrorInfo, ReactNode } from 'react';
import React, { Component } from 'react';
import logger from '../services/logger';
import * as Sentry from '@sentry/react';
import i18n from '../i18n';

interface Props {
  children: ReactNode;
  /** Name of the page for error reporting (e.g. 'LearnView') */
  pageName: string;
  /** Optional callback to reset page state (e.g. navigate to home tab) */
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Page-level Error Boundary
 * 
 * Unlike the root ErrorBoundary which replaces the ENTIRE screen,
 * this boundary only replaces the PAGE CONTENT while keeping
 * the navigation tabs intact. This allows users to navigate
 * away from a broken page without reloading the whole app.
 */
class PageErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error(this.props.pageName, 'Page crash', error);

    // Report to Sentry with page context
    Sentry.captureException(error, {
      extra: {
        pageName: this.props.pageName,
        componentStack: errorInfo.componentStack,
      },
      tags: { page: this.props.pageName },
    });

    // Also report to our own backend
    try {
      fetch('/api/log-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `[${this.props.pageName}] ${error.message}`,
          stack: error.stack,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: Date.now(),
        }),
      }).catch(() => {});
    } catch {
      // Fire and forget — don't let error reporting crash the error boundary
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 24px',
          textAlign: 'center',
          minHeight: 300,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
          <h2 style={{
            fontSize: 18,
            fontWeight: 700,
            margin: '0 0 8px',
            color: 'var(--text, #111)',
          }}>
            {i18n.t('error_something_wrong')}
          </h2>
          <p style={{
            fontSize: 14,
            color: 'var(--text-muted, #888)',
            margin: '0 0 24px',
            lineHeight: 1.5,
            maxWidth: 320,
          }}>
            {i18n.t('error_progress_saved')}
          </p>

          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={this.handleRetry}
              style={{
                background: 'var(--primary, #E03705)',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                padding: '10px 20px',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              🔄 {i18n.t('error_try_again')}
            </button>

            {this.props.onReset && (
              <button
                onClick={this.handleReset}
                style={{
                  background: 'var(--surface, #f5f5f5)',
                  color: 'var(--text, #333)',
                  border: '1px solid var(--border, #ddd)',
                  borderRadius: 10,
                  padding: '10px 20px',
                  fontSize: 14,
                  cursor: 'pointer',
                }}
              >
                🏠 {i18n.t('error_go_back')}
              </button>
            )}
          </div>

          {process.env.NODE_ENV !== 'production' && this.state.error && (
            <details style={{
              marginTop: 24,
              textAlign: 'left',
              background: 'rgba(255,0,0,0.05)',
              borderRadius: 8,
              padding: 12,
              width: '100%',
              maxWidth: 400,
            }}>
              <summary style={{ color: '#999', cursor: 'pointer', fontSize: 12 }}>
                Debug: {this.props.pageName}
              </summary>
              <pre style={{
                color: '#e74c3c',
                fontSize: 11,
                overflow: 'auto',
                marginTop: 8,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}>
                {this.state.error.message}
                {'\n'}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default PageErrorBoundary;
